import { useDispatch } from "react-redux";
import { useSelector } from "store/useSelector";
import {
  updateShifts,
  goToDay,
  setShouldFetchNewShifts,
} from "../../../providers/GlobalStateProvider";
import { useEffect, useMemo, useRef, useTransition } from "react";
import { isEqual } from "lodash";
import { usePrevious } from "../../../constants/constants";
import {
  askrClients,
  SHIFT_SEGMENTS_QUERY,
} from "../../../providers/AskrProvider";
import {
  BACKWARD_PADDING,
  FORWARD_PADDING,
} from "./steppable-range-selectors/ShiftSelector/shiftUtils";
import { useSettings } from "../../../contexts/SettingsManager/SettingsManager";
import { FETCH_POLICY } from "../../../segments/AggregatesGetter/aggregatesGetterUtils";
import { DEFAULT_SHIFTS_GETTER_UPDATE_INTERVAL } from "../../../constants/intervals";
import useQueryWithErrorHandling from "../../../utils/useQuery";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useIsCustomRangeSelector,
  useFocusRange,
  useGetPageFromPath,
  useDashboardPageContext,
  useShifts,
  useFormatZoned,
  useVisibleProcesses,
  useTrendsPageFilterState,
} from "utils/customHooks/genericHooks";
import { ONE_HOUR_OF_MS, ONE_SECOND_OF_MS } from "utils/timeUtils";
import {
  useShouldMockShiftEnd,
  useShouldMockHourEnd,
} from "utils/customHooks/genericHooks";
import { SHIFTS_QUERY_TITLE } from "contexts/ErrorManager/errorManagerUtils";
import { useDayStartChanged } from "store/atoms";
import { useTimeZoneMsOffset } from "utils/timeZoneHooks";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

type ShiftsGetterProps = {
  shouldGoToCurrentShiftOnFetch: boolean;
};

const ShiftsGetter = ({ shouldGoToCurrentShiftOnFetch }: ShiftsGetterProps) => {
  const shouldFetchNewShifts = useSelector(
    state =>
      state.legacy.shouldFetchNewShifts ||
      // always fetch if can & we have none
      (state.customer.ckey && state.legacy.shifts.length === 0)
  );
  const focusRange = useFocusRange();
  const dispatch = useDispatch();
  const ckey = useCustomerKey();
  const { trendsPageFilterState } = useTrendsPageFilterState();
  const { isTrendsPage } = useGetPageFromPath();

  const shifts = useShifts();
  const visibleProcesses = useVisibleProcesses();

  const prevCustomerKey = usePrevious(ckey);
  const prevProcesses: any = usePrevious(visibleProcesses);
  const isNewWorkSchedule =
    visibleProcesses && prevProcesses
      ? !isEqual(
          prevProcesses[0]?.workScheduleConfig?.id,
          visibleProcesses[0]?.workScheduleConfig.id
        )
      : false;

  const isDifferentCustomer = !isEqual(prevCustomerKey, ckey);
  const isNewShiftSchedule = isDifferentCustomer || isNewWorkSchedule;

  const askrClient = askrClients.get(ckey);

  const isFirstShiftsQueryRef = useRef(!isTrendsPage);

  // if dayStartMsOffset changed, update shifts
  const [dayStartChanged, setDayStartChanged] = useDayStartChanged();
  const [, startTransition] = useTransition();

  const workScheduleChanged =
    isTrendsPage &&
    trendsPageFilterState.workScheduleId != shifts[0]?.shift.workScheduleId;
  useEffect(() => {
    if (dayStartChanged || workScheduleChanged) {
      isFirstShiftsQueryRef.current = true;
      setDayStartChanged(false);
      startTransition(() => {
        dispatch(setShouldFetchNewShifts(true));
      });
    }
  }, [dayStartChanged, dispatch, setDayStartChanged, workScheduleChanged]);

  // when the focus range changes, check if we need new shifts
  const attempts = useRef(0);
  useEffect(() => {
    const [firstShift, lastShift] = [shifts[0], shifts[shifts.length - 1]];
    if (!focusRange || !firstShift || !lastShift) {
      return;
    }
    const doesFocusRangeStartBeforeFirstShift =
      focusRange.ge <= firstShift.range.ge;
    const doesFocusRangeEndAfterLastShift = focusRange.lt >= lastShift.range.lt;
    const isFocusRangeOutsideOfShifts =
      doesFocusRangeStartBeforeFirstShift || doesFocusRangeEndAfterLastShift;
    if (isFocusRangeOutsideOfShifts && attempts.current < 6) {
      attempts.current++;
      startTransition(() => {
        dispatch(setShouldFetchNewShifts(true));
      });
    } else if (!isFocusRangeOutsideOfShifts) {
      attempts.current = 0;
    } else if (isFocusRangeOutsideOfShifts && attempts.current > 3) {
      console.log("⚠️ unable to find shifts for focus range", {
        focusRange: {
          ge: `${new Date(focusRange.ge)}, ${focusRange.ge}`,
          lt: `${new Date(focusRange.lt)}, ${focusRange.lt}`,
        },
        firstShift: {
          ge: `${new Date(firstShift.range.ge)}, ${firstShift.range.ge}`,
          lt: `${new Date(firstShift.range.lt)}, ${firstShift.range.lt}`,
        },
        doesFocusRangeStartBeforeFirstShift,
        doesFocusRangeEndAfterLastShift,
      });
    }
  }, [focusRange, dispatch, shifts]);

  // when the ckey or workschedule changes, fetch new shifts
  useEffect(() => {
    if (isNewShiftSchedule) {
      startTransition(() => {
        dispatch(setShouldFetchNewShifts(true));
        isFirstShiftsQueryRef.current = true;
      });
    }
  }, [dispatch, isNewShiftSchedule]);

  const now = useRef(Date.now());

  let ge = null as string | null;
  let lt = null as string | null;

  let processName: string | undefined = undefined;
  let machemetrySourceName: string | undefined = undefined;

  if (visibleProcesses && visibleProcesses.length >= 1) {
    processName = visibleProcesses[0].name;
    machemetrySourceName = visibleProcesses[0].id;
  }

  //if (shouldFetchNewShifts) {
  //TODO confirm new should fetch logic
  if (shouldFetchNewShifts || processName) {
    ge = String(
      // we need to query once without the focus range to determine the focus range
      (isFirstShiftsQueryRef.current || !focusRange
        ? now.current
        : focusRange.ge) - BACKWARD_PADDING
    );

    lt = String(
      (isFirstShiftsQueryRef.current || !focusRange
        ? now.current
        : focusRange.lt) + FORWARD_PADDING
    );
  }

  const shouldQueryForShifts = shouldFetchNewShifts && ge && lt;

  return shouldQueryForShifts ? (
    <Query
      {...{
        ge,
        lt,
        machemetrySourceName,
        processName,
        askrClient,
        ckey,
        focusRange,
        isFirstShiftsQueryRef,
        shouldGoToCurrentShiftOnFetch,
        now,
      }}
    />
  ) : null;
};

function Query({
  ge,
  lt,
  machemetrySourceName,
  processName,
  askrClient,
  ckey,
  focusRange,
  isFirstShiftsQueryRef,
  shouldGoToCurrentShiftOnFetch,
  now,
}: any) {
  const variables = useMemo(
    () => ({
      ge: ge,
      lt: lt,
      processName: processName,
      machemetrySourceName: machemetrySourceName,
    }),
    [ge, lt, processName, machemetrySourceName]
  );

  useQueryAndUpdateAskrShifts({
    query: SHIFT_SEGMENTS_QUERY,
    variables,
    client: askrClient,
    pollInterval: DEFAULT_SHIFTS_GETTER_UPDATE_INTERVAL,
    onError: _err => {
      console.log(
        `Shifts query gone wrong. cKey: ${ckey}, ge: ${ge}, lt: ${lt}`,
        focusRange
          ? `focusRange: { ge: ${focusRange.ge}, lt: ${focusRange.lt} }`
          : isFirstShiftsQueryRef.current
            ? `first shifts query (so no focusRange is set)`
            : ``
      );
    },
    isFirstShiftsQueryRef,
    shouldGoToCurrentShiftOnFetch,
    now,
  });
  return null;
}

export default ShiftsGetter;

type queryAndUpdateAskrShiftsProps = {
  skip?: boolean;
  shouldGoToCurrentShiftOnFetch: boolean;
  query: any;
  client: any;
  onError: any;
  variables: any;
  pollInterval?: number | null;
  isFirstShiftsQueryRef: { current: boolean };
  now: { current: number };
};
// tslint:disable-next-line: cognitive-complexity
function useQueryAndUpdateAskrShifts({
  skip = false,
  query,
  variables,
  client,
  pollInterval = null,
  onError,
  isFirstShiftsQueryRef,
  shouldGoToCurrentShiftOnFetch,
  now,
}: queryAndUpdateAskrShiftsProps) {
  const { isGridLayoutPage, isTrendsPage, isOperatorPage } =
    useGetPageFromPath();
  const shouldFetchNewShifts = useSelector(
    state => state.legacy.shouldFetchNewShifts
  );
  const dispatch = useDispatch();
  const [, startTransition] = useTransition();
  const shouldMockShiftEnd = useShouldMockShiftEnd();
  const shouldMockHourEnd = useShouldMockHourEnd();
  const mockShiftsEndingSoon = useMockShiftsEndingSoon();
  const mockShiftsEndingHourSoon = useMockShiftsHourEndingSoon();
  const isCustomRangeSelector = useIsCustomRangeSelector();
  const isPageWithDatePickerAndShifts = !isTrendsPage && !isGridLayoutPage;
  const { dayStartMsOffset } = useSettings();
  const timeZoneMsOffset = useTimeZoneMsOffset();
  const { isFullDaySelected } = useDashboardPageContext();
  const formatZoned = useFormatZoned();
  const [
    {
      general: {
        operatorPageEnableRangeSelector,
        multiplyNetworkRequestIntervals,
      },
    },
  ] = useFlightConfig();

  return useQueryWithErrorHandling(SHIFTS_QUERY_TITLE, query, {
    // run it if the trends page is not filtered to "all" shifts
    skip,
    ...(pollInterval
      ? { pollInterval: pollInterval * multiplyNetworkRequestIntervals }
      : {}),
    client,
    variables,
    fetchPolicy: FETCH_POLICY,
    onError,
    onCompleted: data => {
      if (data) {
        // if the ShiftsGetter has already been unmounted before we've received a query response,
        // then when we do receive a response, don't do anything with it because we don't care about it anymore

        const shouldGoToCurrentShift =
          !isTrendsPage &&
          !isCustomRangeSelector && // custom range selector doesn't go to shifts
          (isFirstShiftsQueryRef.current || // go to current shift on first fetch
            (!shouldGoToCurrentShiftOnFetch && // don't go to current shift when navigating to certain pages e.g. timeline -> hublet
              !shouldFetchNewShifts)); // don't go if we're fetching new shifts e.g. picked a date far into the past from calendar

        now.current = Date.now();
        const shouldSetFocusRange = !isTrendsPage && !isCustomRangeSelector;
        // startTransition(() => {
        dispatch(
          updateShifts({
            shifts: shouldMockShiftEnd
              ? mockShiftsEndingSoon
              : shouldMockHourEnd
                ? mockShiftsEndingHourSoon
                : data.shiftSegments,
            dayStartMsOffset,
            timeZoneMsOffset,
            // only go to the current shift on the first fetch (per customer) or if leaving the details page
            shouldGoToCurrentShift,
            shouldSetFocusRange,
            isFullDaySelected,
            formatZoned,
          })
        );
        // });
        // if we're fetching more shifts later on e.g. from date picker calendar,
        if (
          isPageWithDatePickerAndShifts &&
          shouldFetchNewShifts &&
          !isFirstShiftsQueryRef.current &&
          !isCustomRangeSelector
        ) {
          // go to current selected day after fetching new shifts
          startTransition(() => {
            dispatch(
              goToDay({
                newSelectedDay: null,
                dayStartMsOffset,
                timeZoneMsOffset,
                shouldGoToFirstShiftInDay: !isFullDaySelected,
                shouldGoToCurrentShift:
                  isOperatorPage && !operatorPageEnableRangeSelector,
                formatZoned,
              })
            );
          });
        }

        if (isFirstShiftsQueryRef.current) {
          isFirstShiftsQueryRef.current = false;
        }
      }
    },
    context: { important: true }, // when batching is set up, force this to be non-batched
  });
}

const SHIFT_DURATION = 8 * ONE_HOUR_OF_MS;
const TIME_TO_SHIFT_END = 10 * ONE_SECOND_OF_MS;
function useMockShiftsEndingSoon() {
  const mockShifts = useMemo(() => {
    const shiftEnd = Date.now() + TIME_TO_SHIFT_END;
    return [
      // cleanShiftSegments slices 2 shifts off each end
      {},
      {},
      {
        shift: { name: "Mock Shift - Ended Already" },
        range: {
          ge: String(shiftEnd - 2 * SHIFT_DURATION),
          lt: String(shiftEnd - SHIFT_DURATION),
        },
      },
      {
        shift: { name: "Mock Shift - Ending Soon" },
        range: { ge: String(shiftEnd - SHIFT_DURATION), lt: String(shiftEnd) },
      },
      {
        shift: { name: "Mock Shift - Starting Soon" },
        range: { ge: String(shiftEnd), lt: String(shiftEnd + SHIFT_DURATION) },
      },
      {
        shift: { name: "Mock Shift - Starting Later" },
        range: {
          ge: String(shiftEnd + SHIFT_DURATION),
          lt: String(shiftEnd + 2 * SHIFT_DURATION),
        },
      },
      {},
      {},
    ];
  }, []);
  return mockShifts;
}

const TIME_TO_HOUR_END = 10 * ONE_SECOND_OF_MS;
function useMockShiftsHourEndingSoon() {
  // use a ref for the current time, so that on refetch we use the same value as last time
  const mockShifts = useMemo(() => {
    const hourEnd = Date.now() + TIME_TO_HOUR_END;
    const shiftEnd = hourEnd + 6 * ONE_HOUR_OF_MS;
    return [
      // cleanShiftSegments slices 2 shifts off each end
      {},
      {},
      {
        shift: { name: "Mock Shift - Ended Already" },
        range: {
          ge: String(shiftEnd - 2 * SHIFT_DURATION),
          lt: String(shiftEnd - SHIFT_DURATION),
        },
      },
      {
        shift: { name: "Mock Shift - Hour Ending Soon" },
        range: { ge: String(shiftEnd - SHIFT_DURATION), lt: String(shiftEnd) },
      },
      {
        shift: { name: "Mock Shift - Starting Next" },
        range: { ge: String(shiftEnd), lt: String(shiftEnd + SHIFT_DURATION) },
      },
      {
        shift: { name: "Mock Shift - Starting Later" },
        range: {
          ge: String(shiftEnd + SHIFT_DURATION),
          lt: String(shiftEnd + 2 * SHIFT_DURATION),
        },
      },
      {},
      {},
    ];
  }, []);
  return mockShifts;
}
